/**
 * Hook: single-action transcript submit (B-13.3 Path a).
 *
 * Two submission modes:
 *   1. JSON paste: POST /interview-analysis/paste-box-with-parse
 *      Body: { candidate_id, jd_id, stage_id, transcript }
 *
 *   2. File upload: POST /interview-analysis/paste-box-upload
 *      Multipart: file + candidate_id + jd_id + stage_id
 *
 * Both endpoints: save transcript + AI parse synchronously → return
 * AnalysisPasteBoxRow with analysis populated. Eliminates the
 * "Transcript loaded" terminal state from PR #168.
 *
 * B-13.3 cofounder brief §1 (kill transcript-loaded state) + §3 (file extraction).
 *
 * Hard rules:
 *   - 402 → out-of-credits toast.
 *   - 422 audio_not_supported → user-readable toast about audio being P1.
 *   - On success, populate paste-box cache so AnalysisInnerState resolves
 *     to analysis-loaded (not transcript-loaded) on next render.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useToastStore } from "@/stores/useToastStore";
import type { AnalysisPasteBoxRow } from "./useAnalysisPasteBox";

interface SubmitTranscriptTextArgs {
  mode: "text";
  candidate_id: string;
  jd_id: string | undefined;
  stage_id: string;
  /** Per-round slot (backend migration 0124). `null`/undefined = unassigned. */
  round_id?: string | null;
  transcript: string;
}

interface SubmitTranscriptFileArgs {
  mode: "file";
  candidate_id: string;
  jd_id: string | undefined;
  stage_id: string;
  round_id?: string | null;
  file: File;
}

type SubmitTranscriptArgs = SubmitTranscriptTextArgs | SubmitTranscriptFileArgs;

export function useSubmitTranscript(): UseMutationResult<
  AnalysisPasteBoxRow,
  ApiError,
  SubmitTranscriptArgs
> {
  const queryClient = useQueryClient();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<AnalysisPasteBoxRow, ApiError, SubmitTranscriptArgs>({
    mutationFn: async (args) => {
      let data: AnalysisPasteBoxRow;

      if (args.mode === "text") {
        const r = await apiClient.post<AnalysisPasteBoxRow>(
          `/api/t/${tenantSlug}/interview-analysis/paste-box-with-parse`,
          {
            candidate_id: args.candidate_id,
            jd_id: args.jd_id ?? null,
            stage_id: args.stage_id,
            round_id: args.round_id ?? null,
            transcript: args.transcript,
          },
        );
        data = r.data;
      } else {
        // File upload via multipart.
        const form = new FormData();
        form.append("file", args.file);
        form.append("candidate_id", args.candidate_id);
        if (args.jd_id) form.append("jd_id", args.jd_id);
        form.append("stage_id", args.stage_id);
        if (args.round_id) form.append("round_id", args.round_id);

        // Override the apiClient JSON default so the browser auto-sets
        // multipart/form-data with the correct boundary for this FormData body.
        const r = await apiClient.post<AnalysisPasteBoxRow>(
          `/api/t/${tenantSlug}/interview-analysis/paste-box-upload`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        data = r.data;
      }

      return {
        ...data,
        saved_at: data.saved_at ?? new Date().toISOString(),
      };
    },
    onSuccess: (data, vars) => {
      // Populate paste-box cache so useAnalysisInnerState resolves
      // to analysis-loaded on the next render cycle.
      queryClient.setQueryData(
        [
          "interview-analysis",
          "paste-box",
          tenantSlug,
          vars.candidate_id,
          vars.jd_id,
          vars.stage_id,
          vars.round_id ?? null,
        ],
        data,
      );
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 402) {
          addToast("Out of credits — top up to continue.", "warning");
          return;
        }
        if (err.status === 422) {
          const errCode = err.error;
          if (errCode === "audio_not_supported") {
            addToast(
              "Audio uploads are not yet supported. Upload a .txt, .pdf, .docx, .vtt, or .srt transcript.",
              "warning",
            );
            return;
          }
          if (errCode === "unsupported_file_type") {
            addToast(
              "Unsupported file type. Accepted: .txt .pdf .docx .vtt .srt",
              "warning",
            );
            return;
          }
          if (errCode === "transcript_extraction_failed" || errCode === "transcript_empty") {
            addToast(
              "Could not extract text from that file. Try a different format or paste the transcript.",
              "warning",
            );
            return;
          }
        }
      }
      // Other errors bubble to the consumer.
    },
  });
}
